'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import { getDb } from '@/db';
import { savedMazes } from '@/db/schema';
import { getJobsPool } from '@/lib/jobsDb';
import { publish, userChannel } from '@/lib/events';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { SessionService } from '@/contexts/authentication';
import { GAMES_JOBS_TENANT, MAZE_QUEUE } from '../infrastructure/mazeGenerationQueue';
import { settleMazeImage, refundMazeImage, mazePricing } from '../infrastructure/mazeBilling';

export type MazeResultStatus =
  | { status: 'pending' }
  | { status: 'failed'; error: string }
  | { status: 'skipped' }
  | {
      status: 'completed';
      maze: {
        id: string;
        mazeType: string;
        difficulty: string;
        geometryBlobUrl: string;
        /** Per-difficulty geometry blob urls (worker ≥ per-level tessellation); absent for old jobs. */
        geometryUrls?: Record<string, string>;
        thumbnailBlobUrl: string;
        templateId?: string;
        sourceImageBlobUrl?: string;
        aspectRatio?: number;
      };
    };

interface JobResultRow {
  status: string | null;
  // NOTE: normally a jsonb object, but the shared worker's outbox currently
  // DOUBLE-ENCODES it (emit_result json.dumps() + a jsonb encoder codec on the
  // pool) so pg hands us a JSON *string* scalar. `normalizeOutput` tolerates both
  // so a spent token still resolves to its maze. See root-cause flag in the PR.
  output: unknown;
  /** The worker's usage fact (image maze: the COGS fact the settle tracks). */
  usage?: unknown;
}

/** Accept either a jsonb object or a (double-encoded) JSON string from the outbox. */
function normalizeOutput(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return {};
}

/**
 * On-demand consume of a single maze result from the shared owlcat_jobs
 * `job_results` outbox (phase-1 alternative to the always-on consumer daemon).
 *
 * Idempotently persists a completed maze into owlcat_games.saved_mazes
 * (id = mazeId, ON CONFLICT DO NOTHING) and pushes an SSE `games.maze.generated`
 * nudge on the caller's own channel so the maze page live-updates.
 */
export async function readMazeResult(mazeId: string): Promise<MazeResultStatus> {
  const ownerId = await SessionService.getFamilyId();
  if (!ownerId) return { status: 'pending' };

  // Scope to the caller's own rows (security) and match mazeId in JS — the SQL
  // `output->>'mazeId'` filter is unusable while the outbox double-encodes output
  // (a JSON string scalar yields NULL for ->>), which would strand every result.
  const { rows } = await getJobsPool().query<JobResultRow>(
    `SELECT status, output, usage
       FROM job_results
      WHERE tenant = $1 AND queue = $2 AND owner_id = $3
      ORDER BY id DESC
      LIMIT 100`,
    [GAMES_JOBS_TENANT, MAZE_QUEUE, ownerId],
  );

  const match = rows
    .map((r) => ({ status: r.status, output: normalizeOutput(r.output), usage: r.usage }))
    .find((r) => r.output.mazeId === mazeId);
  if (!match) return { status: 'pending' };

  const row = match;
  const output = row.output;

  if (row.status === 'failed') {
    // Release any image-maze hold (no-op for free template mazes / missing holds).
    try {
      await refundMazeImage(mazeId, 'Image maze generation failed');
    } catch (e) {
      actionErrorLog('readMazeResult.refund', e);
    }
    return { status: 'failed', error: String(output.error ?? 'Maze generation failed') };
  }
  if (row.status === 'skipped') return { status: 'skipped' };
  if (row.status !== 'completed') return { status: 'pending' };

  const geometryBlobUrl = String(output.geometryBlobUrl ?? '');
  const thumbnailBlobUrl = String(output.thumbnailBlobUrl ?? '');
  const mazeType = String(output.mazeType ?? 'template-maze');
  const difficulty = String(output.difficulty ?? 'medium');
  const templateId = (output.templateId as string | null) ?? null;
  const sourceImageBlobUrl = (output.sourceImageBlobUrl as string | null) ?? null;
  const aspectRatio = typeof output.aspectRatio === 'number' ? output.aspectRatio : 1.0;
  const geometryUrls = (() => {
    const raw = output.geometryByDifficulty;
    if (!raw || typeof raw !== 'object') return undefined;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (typeof v === 'string' && v) out[k] = v;
    return Object.keys(out).length ? out : undefined;
  })();

  // INVARIANT (paid ⇒ persisted): persist the maze row FIRST, then settle. A
  // spent token must ALWAYS leave behind a retrievable maze in history — so we
  // never settle a hold before the row exists. If the persist throws (DB down),
  // we do NOT settle and report `pending`: the hold stays open and the caller's
  // poll retries (and refunds on final timeout), so no token is charged without
  // a persisted result. Idempotent (keyed by mazeId, ON CONFLICT DO NOTHING) —
  // a row already present from a prior drain still satisfies the invariant.
  try {
    await getDb()
      .insert(savedMazes)
      .values({
        id: mazeId,
        familyId: ownerId,
        mazeType,
        difficulty,
        geometryBlobUrl,
        thumbnailBlobUrl,
        templateId,
        sourceImageBlobUrl,
        geometryUrls: geometryUrls ?? null,
      })
      .onConflictDoNothing({ target: savedMazes.id });
  } catch (e) {
    actionErrorLog('readMazeResult.persist', e);
    // Not persisted → do NOT settle. Report pending so the hold is preserved and
    // the caller keeps polling (eventual timeout refunds).
    return { status: 'pending' };
  }

  // Row is persisted — NOW settle the image-maze hold as the flat price
  // (idempotent; template mazes are free and have no reservation). Best-effort:
  // a billing hiccup must not hide a maze the user can already see, and the hold
  // otherwise expires server-side; a later drain/daemon re-settles.
  if (mazeType === 'image-maze') {
    try {
      await settleMazeImage(mazeId, (await mazePricing()).imagePriceTokens, row.usage);
    } catch (e) {
      actionErrorLog('readMazeResult.settle', e);
    }
  }

  // SSE nudge on the caller's own channel — the maze page fetches the geometry.
  try {
    await publish(userChannel(ownerId), {
      type: 'games.maze.generated',
      payload: {
        mazeId,
        mazeType,
        geometryUrl: mediaSrc(geometryBlobUrl),
        geometryUrls: geometryUrls
          ? Object.fromEntries(Object.entries(geometryUrls).map(([k, v]) => [k, mediaSrc(v)]))
          : undefined,
        imageUrl: sourceImageBlobUrl ?? undefined,
        aspectRatio,
      },
    });
  } catch (e) {
    actionErrorLog('readMazeResult.sse', e);
  }

  return {
    status: 'completed',
    maze: {
      id: mazeId,
      mazeType,
      difficulty,
      geometryBlobUrl,
      geometryUrls,
      thumbnailBlobUrl,
      templateId: templateId ?? undefined,
      sourceImageBlobUrl: sourceImageBlobUrl ?? undefined,
      aspectRatio,
    },
  };
}
